import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import jsQR from "jsqr";
import AfterSalesLayout from "../aftersaleslayout/AfterSalesLayout";
import { ChevronLeft, TriangleAlert } from "lucide-react";
import Shield from "../../../assets/aftersalesservice_icons/shield.svg";
import QrScanned from "../../../assets/aftersalesservice_icons/qr_scanned_icon.svg";
import QrScanned2 from "../../../assets/aftersalesservice_icons/qr_scanned_2_icon.svg";
import ScanQr from "../../../assets/aftersalesservice_icons/scanqr_icon.svg";
import Container from "../../../assets/aftersalesservice_icons/container.svg";
import UploadBlue from "../../../assets/aftersalesservice_icons/upload-blue.svg";
import InvAttached from "../../../assets/aftersalesservice_icons/inv_attached_icon.svg";
import Human from "../../../assets/aftersalesservice_icons/human.svg";
import ApiService from "../../../components/Api_services";
import MultiPurpose from "../aftersales_multipurpose/MultiPurpose";
import RegisteredReceived from "../registeredreceived/RegisteredReceived";
import {
  PHONE_COUNTRY_OPTIONS,
  inferCountryFromName,
  phoneCountryFromCountryCode,
  splitPhoneInput,
  isValidRegionalPhone,
  detectCountryFromFullNumber,
} from "../warrantyPhoneUtils";
import { useLanguage } from "../LanguageContext";
import {
  buildAfterSalesQuery,
  getAfterSalesApiSerial,
  getAfterSalesSerialType,
  parseQrSerialPayload,
} from "../serialTypeUtils";

// Find the unit's own row in a get_serial_number item list.
// Type 2 QR carries an AES token that only the API can decrypt, so match the
// single row that has a serial (sibling rows come back with an empty serial_no).
const findSerialItem = (items, apiSerial, serialType) => {
  const list = Array.isArray(items) ? items : [];
  if (serialType !== "2") {
    return list.find((it) => it.serial_no === apiSerial);
  }
  const withSerial = list.filter((it) => it.serial_no);
  return withSerial.length === 1 ? withSerial[0] : undefined;
};

const RegisterSerialNoBrunei = () => {
  const {
    trans,
    lang,
    countryCode: contextCountryCode,
    warrantyLoaded,
  } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const serialNoForApi = getAfterSalesApiSerial(searchParams);
  const serialType = getAfterSalesSerialType(searchParams);

  const [customerData, setCustomerData] = useState("");
  const [warrantyInfo, setWarrantyInfo] = useState({
    warrantyitemlist: "",
    salesorder: "",
    custname: "",
    custtelno: "",
    emailadd: "",
    country: "",
    state: "",
    city: "",
    postcode: "",
    street: "",
    invoiceno: "",
    purchasedate: "",
  });
  const [loading, setLoading] = useState(false);
  const [notifMessage, setNotifMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const [agreeWarrantyPolicy, setAgreeWarrantyPolicy] = useState(false);
  const [warrantyPolicyPdf, setWarrantyPolicyPdf] = useState(null);
  const [errors, setErrors] = useState({
    custname: "",
    custtelno: "",
    invattach: "",
  });
  const [cleanPhoneNumber, setCleanPhoneNumber] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("BN");
  // qrscanner states
  const cameraStreamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const uploadInitializedRef = useRef(false);
  const [showModal, setShowModal] = useState(false);
  const [activeScannerTab, setActiveScannerTab] = useState("camera");
  const [scannedUnit, setScannedUnit] = useState(null);
  const sn2 = scannedUnit?.serial_no || "";
  const [scanError, setScanError] = useState("");
  const scanCooldownRef = useRef(false);
  const unit1Ref = useRef({ serial_no: "", type: "", otherLabel: "" });
  const [showBuyerSection, setShowBuyerSection] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const invoiceInputRef = useRef(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  useEffect(() => {
    const fetchWarrantyInfo = async () => {
      const serialNo = serialNoForApi;

      try {
        const response = await ApiService.getWarrantyInfo(serialNo, serialType);
        // console.log("warranty info:", response);

        if (response && response.status === 200) {
          const items = response.data?.warranty_item_list || [];
          // ensure each item has serial_no
          const normalizedItems = (Array.isArray(items) ? items : []).map(
            (it) => {
              const { item_warranty_period, ...rest } = it || {};
              return {
                ...rest, // rest no longer contains item_warranty_period
                // map item_warranty_period -> warranty_day (ensure numeric)
                warranty_day:
                  rest?.warranty_day ??
                  (item_warranty_period != null
                    ? Number(item_warranty_period)
                    : 0),
              };
            },
          );

          const customer = response.data?.customer_data || null;
          const phoneRaw = customer?.phone_no || "";
          const defaultCountry =
            phoneCountryFromCountryCode(contextCountryCode) ||
            inferCountryFromName(customer?.country) ||
            "BN";
          const normalized = splitPhoneInput(phoneRaw, defaultCountry);
          setPhoneCountry(normalized.countryId || defaultCountry);
          setCleanPhoneNumber(normalized.digit_only);

          const updateFields = {
            warrantyitemlist: normalizedItems,
            salesorder: customer?.sales_order || "",
            custname: customer?.receiver_name || "",
            custtelno: normalized.nationalDisplay || normalized.national || "",
            country: customer?.country || "",
            state: customer?.province || "",
            city: customer?.city || "",
            postcode: customer?.zip_code || "",
            street: customer?.address || "",
            invoiceno: customer?.shopee_order_id || "",
            purchasedate: customer?.purchase_date || "",
          };

          setWarrantyInfo((prev) => ({ ...prev, ...updateFields }));
          setCustomerData(response.data?.customer_data);
        }
      } catch (error) {
        console.error("Error fetching warranty info:", error);
      }
    };

    // wait for LanguageContext so countryCode can pick the default phone country
    if (!warrantyLoaded) return;
    fetchWarrantyInfo();
  }, [serialNoForApi, serialType, warrantyLoaded, contextCountryCode]);

  const warrantyItems = Array.isArray(warrantyInfo.warrantyitemlist)
    ? warrantyInfo.warrantyitemlist
    : [];
  const itemId1 = warrantyItems[0]?.item_id;
  const itemId2 = warrantyItems[1]?.item_id;
  const countryCode = warrantyItems[0]?.country_code || "";

  useEffect(() => {
    const fetchWarrantyPolicyPdf = async () => {
      const item_id = [itemId1, itemId2].filter(Boolean).map(Number);
      if (item_id.length === 0) return;
      const apiLang = lang === "ch" ? "cn" : lang === "my" ? "ms" : lang;

      try {
        const response = await ApiService.getWarrantyPolicy(
          item_id,
          apiLang,
          countryCode,
        );
        if (response && response.status === 200) {
          setWarrantyPolicyPdf(response.data[0]?.pdf_file || null);
        } else {
          setWarrantyPolicyPdf(null);
          console.error("Error fetching warranty policy:", response.message);
        }
      } catch (error) {
        setWarrantyPolicyPdf(null);
        console.error("Error fetching warranty policy:", error);
      }
    };

    fetchWarrantyPolicyPdf();
  }, [itemId1, itemId2, countryCode, lang]);

  const openPdf = (e) => {
    e.preventDefault();
    if (warrantyPolicyPdf) window.open(warrantyPolicyPdf, "_blank");
  };

  useEffect(() => {
    if (customerData?.shopee_order_id) {
      navigate(`/aftersalesservice${buildAfterSalesQuery(searchParams)}`);
    }
  }, [customerData]);

  useEffect(() => {
    const fetchRegisterStatus = async () => {
      const serialNo = serialNoForApi;

      try {
        const response = await ApiService.checkRegisterStatus(
          serialNo,
          serialType,
        );

        if (response) {
          if (response.status === 200) {
            navigate(
              `/aftersalesservice/menucenter${buildAfterSalesQuery(searchParams)}`,
            );
          }
        }
      } catch (error) {
        console.error("Error fetching register status:", error);
      }
    };

    fetchRegisterStatus();
  }, [serialNoForApi, serialType]);

  // Determine serial labels based on scanned SN type
  const matchedItem = findSerialItem(warrantyItems, serialNoForApi, serialType);
  // Plain serial of unit 1 (type 2 URL holds an encrypted token, never shown/submitted)
  const sn =
    matchedItem?.serial_no || (serialType === "2" ? "" : serialNoForApi);
  const isOutdoor = matchedItem?.type?.toUpperCase() === "OUTDOOR";
  const unit1Type = isOutdoor ? "OUTDOOR" : "INDOOR";
  const unit_1 = isOutdoor ? trans.outdoor_unit : trans.indoor_unit;
  const unit_2 = isOutdoor ? trans.indoor_unit : trans.outdoor_unit;

  // Scanner callbacks run from a setInterval closure — read unit 1 from a ref
  useEffect(() => {
    unit1Ref.current = { serial_no: sn, type: unit1Type, otherLabel: unit_2 };
  }, [sn, unit1Type, unit_2]);

  useEffect(() => {
    if (!scanError) return;
    const timer = setTimeout(() => setScanError(""), 3000);
    return () => clearTimeout(timer);
  }, [scanError]);

  const handlePhoneInput = (value) => {
    const split = splitPhoneInput(value, phoneCountry);
    const digits = (value || "").replace(/[^\d]/g, "");
    if (detectCountryFromFullNumber(digits)) {
      setPhoneCountry(split.countryId);
    }
    setCleanPhoneNumber(split.digit_only);
    setWarrantyInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || "",
    }));
    setErrors((prev) => ({
      ...prev,
      custtelno: "",
    }));
  };

  const handlePhoneBlur = (value) => {
    const trimmed = value.trim();
    if (trimmed === "") return;

    const split = splitPhoneInput(trimmed, phoneCountry);
    if (detectCountryFromFullNumber(trimmed.replace(/[^\d]/g, ""))) {
      setPhoneCountry(split.countryId);
    }
    setCleanPhoneNumber(split.digit_only);
    setWarrantyInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || trimmed,
    }));
  };

  const handlePhoneCountryChange = (countryId) => {
    setPhoneCountry(countryId);
    if (!warrantyInfo.custtelno?.trim()) return;

    const split = splitPhoneInput(warrantyInfo.custtelno, countryId);
    setCleanPhoneNumber(split.digit_only);
    setWarrantyInfo((prev) => ({
      ...prev,
      custtelno: split.nationalDisplay || split.national || prev.custtelno,
    }));
    setErrors((prev) => ({
      ...prev,
      custtelno: "",
    }));
  };

  const handleSubmit = async () => {
    if (loading) return;
    setLoading(true);
    setNotifMessage("");

    let newErrors = { custname: "", custtelno: "", invattach: "" };
    let hasError = false;

    if (!warrantyInfo.custname) {
      newErrors.custname = "Please enter buyer name";
      hasError = true;
    }

    if (!warrantyInfo.custtelno) {
      newErrors.custtelno = "Please enter mobile number";
      hasError = true;
    } else if (!isValidRegionalPhone(warrantyInfo.custtelno, phoneCountry)) {
      newErrors.custtelno = "Invalid mobile number format for selected country";
      hasError = true;
    }

    if (!invoiceFile) {
      newErrors.invattach = "Please upload invoice attachment";
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) {
      setLoading(false);
      return;
    }

    const submitPhone =
      cleanPhoneNumber ||
      splitPhoneInput(warrantyInfo.custtelno, phoneCountry).digit_only;

    if (!sn || !scannedUnit) {
      setNotifMessage("Please scan both units before submitting.");
      setShowMessage(true);
      setLoading(false);
      return;
    }

    // Prepare warranty_item_list with only required fields — the scanned pair,
    // plain serials, each with its own warranty (same as NewInstallation)
    const warranty_item_list = [
      { serial_no: sn, warranty: matchedItem?.warranty },
      scannedUnit,
    ].map((unit) => {
      const mappedItem = { serial_no: unit.serial_no };

      // Only include warranty if it exists and has data
      if (unit.warranty && unit.warranty.length > 0) {
        mappedItem.warranty = unit.warranty.map((w) => ({
          name: w.name,
          duration: w.duration,
        }));
      }

      return mappedItem;
    });

    const formData = new FormData();
    formData.append("name", warrantyInfo.custname);
    formData.append("phone", submitPhone);
    formData.append("phone_country", phoneCountry);
    formData.append("email", warrantyInfo.emailadd || "");
    formData.append("warranty_item_list", JSON.stringify(warranty_item_list));
    formData.append("serial_type", serialType);
    if (invoiceFile) {
      formData.append("file", invoiceFile);
    }

    try {
      const response = await ApiService.registerWarranty(formData);
      if (response && response.status === 200) {
        setShowSuccessMessage(true);
      } else {
        console.error("Error submitting registration:", response);
        // Handle API error message if available
        const errorMessage =
          response?.message || "Failed to submit registration.";
        setNotifMessage(errorMessage);
        setShowMessage(true);
      }
    } catch (error) {
      console.error("Error submitting registration:", error);
      // Handle different types of errors
      let errorMessage = "Failed to submit registration.";

      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setNotifMessage(errorMessage);
      setShowMessage(true);
    } finally {
      setLoading(false);
    }
  };

  // ── qrScanner ───────────────────────────────────────────────────────────

  const stopCameraScanner = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    const qrReader = document.getElementById("qr-reader");
    if (qrReader) qrReader.innerHTML = "";
  };

  const initializeCameraScanner = () => {
    // Release any previous session before opening a new one
    stopCameraScanner();

    const video = document.createElement("video");
    video.setAttribute("playsinline", true);
    video.style.width = "100%";
    video.style.height = "250px";

    const qrReader = document.getElementById("qr-reader");
    qrReader.innerHTML = "";
    qrReader.appendChild(video);

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        // Scanner closed/restarted while waiting for permission — release this stream
        if (cameraStreamRef.current || !video.isConnected) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        cameraStreamRef.current = stream;
        video.srcObject = stream;
        video.play();

        scanIntervalRef.current = setInterval(() => {
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const imageData = ctx.getImageData(
              0,
              0,
              canvas.width,
              canvas.height,
            );
            const code = jsQR(imageData.data, canvas.width, canvas.height);
            if (code) {
              processScannedQR(code.data);
            }
          }
        }, 500);
      })
      .catch((err) => {
        console.error("Camera error:", err);
        alert("Unable to access camera. Please try uploading a photo instead.");
      });
  };

  const initializePhotoUpload = () => {
    if (uploadInitializedRef.current) return;

    const fileInput = document.getElementById("qr-file-input");
    const dropZone = document.getElementById("upload-drop-zone");
    const uploadPreview = document.getElementById("upload-preview");
    const previewImage = document.getElementById("preview-image");
    const removeBtn = document.getElementById("remove-image");

    const resetUpload = () => {
      fileInput.value = "";
      previewImage.src = "";
      dropZone.style.display = "flex";
      uploadPreview.style.display = "none";
    };

    const handleImageFile = (file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        previewImage.src = e.target.result;
        dropZone.style.display = "none";
        uploadPreview.style.display = "block";
        scanQRFromImage(e.target.result);
      };
      reader.readAsDataURL(file);
    };

    dropZone.onclick = () => fileInput.click();
    fileInput.onchange = (e) => {
      if (e.target.files[0]) handleImageFile(e.target.files[0]);
    };
    dropZone.ondragover = (e) => {
      e.preventDefault();
      dropZone.classList.add("as-dragover");
    };
    dropZone.ondragleave = () => dropZone.classList.remove("as-dragover");
    dropZone.ondrop = (e) => {
      e.preventDefault();
      dropZone.classList.remove("as-dragover");
      if (e.dataTransfer.files[0]?.type.startsWith("image/")) {
        handleImageFile(e.dataTransfer.files[0]);
      }
    };
    if (removeBtn) removeBtn.onclick = resetUpload;

    uploadInitializedRef.current = true;
  };

  const switchScannerTab = (tab) => {
    setActiveScannerTab(tab);
    if (tab === "camera") {
      initializeCameraScanner();
    } else {
      stopCameraScanner();
      initializePhotoUpload();
    }
  };

  const scanQRFromImage = (dataUrl) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, canvas.width, canvas.height);
      if (code) {
        // Uploads are user-triggered — no need to hold the lock after a failure
        processScannedQR(code.data, 0);
      } else {
        setScanError(
          "No QR Code Found. Please try a clearer image or use the camera scanner.",
        );
      }
    };
    img.onerror = () => setScanError("Failed to load image.");
    img.src = dataUrl;
  };

  const processScannedQR = async (qrData, retryDelay = 3000) => {
    if (scanCooldownRef.current) return;
    scanCooldownRef.current = true;

    // Keep the lock briefly after a failure so the camera doesn't re-read the same QR every frame
    const failScan = (message) => {
      setScanError(message);
      setTimeout(() => {
        scanCooldownRef.current = false;
      }, retryDelay);
    };

    try {
      const { apiSerial: decodedSerial, serialType: scannedSerialType } =
        parseQrSerialPayload(qrData);

      if (!decodedSerial) {
        failScan("Invalid QR code data");
        return;
      }

      const unit1 = unit1Ref.current;
      if (!unit1.serial_no) {
        failScan("Unable to load the first unit. Please refresh the page.");
        return;
      }

      if (decodedSerial === serialNoForApi) {
        failScan(trans.new_install_unit_2_error);
        return;
      }

      const response = await ApiService.getWarrantyInfo(
        decodedSerial,
        scannedSerialType,
      );

      if (!response || response.status !== 200) {
        failScan(response?.message || "Serial number not found");
        return;
      }

      const matched = findSerialItem(
        response.data?.warranty_item_list,
        decodedSerial,
        scannedSerialType,
      );

      if (!matched) {
        failScan("Serial number not found in item list");
        return;
      }

      if (matched.serial_no === unit1.serial_no) {
        failScan(trans.new_install_unit_2_error);
        return;
      }

      const unitType =
        matched.type?.toUpperCase() === "OUTDOOR" ? "OUTDOOR" : "INDOOR";

      if (unitType === unit1.type) {
        failScan(`${trans.reg_unit_2_type_error}${unit1.otherLabel}`);
        return;
      }

      const bundle = await ApiService.checkUnitsBundle([
        { serial_no: unit1.serial_no, type: unit1.type },
        { serial_no: matched.serial_no, type: unitType },
      ]);

      if (!bundle || bundle.status !== 200) {
        failScan(bundle?.message || "Unable to verify scanned units.");
        return;
      }

      setScannedUnit({
        serial_no: matched.serial_no,
        type: unitType,
        item_code: matched.item_code,
        warranty: matched.warranty,
      });
      stopScanner();
      setShowBuyerSection(true);
    } catch (error) {
      console.error("Error processing QR code:", error);
      failScan("Error processing QR code data.");
    }
  };

  const stopScanner = () => {
    stopCameraScanner();
    setShowModal(false);
    setScanError("");
    scanCooldownRef.current = false;
    uploadInitializedRef.current = false;

    const fileInput = document.getElementById("qr-file-input");
    const previewImage = document.getElementById("preview-image");
    const dropZone = document.getElementById("upload-drop-zone");
    const uploadPreview = document.getElementById("upload-preview");

    if (fileInput) fileInput.value = "";
    if (previewImage) previewImage.src = "";
    if (dropZone) dropZone.style.display = "flex";
    if (uploadPreview) uploadPreview.style.display = "none";
  };

  // Open scanner modal
  const openScanner = () => {
    uploadInitializedRef.current = false;
    setShowModal(true);
    setActiveScannerTab("camera");
    // Wait for modal to render before initializing camera
    setTimeout(() => initializeCameraScanner(), 100);
  };

  const handleInvoiceUploadClick = () => {
    if (!invoiceFile) {
      invoiceInputRef.current.click();
    }
  };

  const handleInvoiceFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("File size must be less than 10MB");
      e.target.value = "";
      return;
    }

    // Validate file type
    const allowedTypes = [
      "application/pdf",
      "image/png",
      "image/jpg",
      "image/jpeg",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("Please upload PDF, PNG, JPG, or JPEG files only");
      e.target.value = "";
      return;
    }

    const isImage = ["image/png", "image/jpg", "image/jpeg"].includes(
      file.type,
    );

    if (isImage) {
      const reader = new FileReader();
      reader.onload = (e) => setInvoicePreview(e.target.result); // base64
      reader.readAsDataURL(file);
    } else {
      setInvoicePreview(null); // PDF — no image preview
    }

    setInvoiceFile(file);
  };

  const handleInvoiceRemove = (e) => {
    e.stopPropagation();
    setInvoiceFile(null);
    setInvoicePreview(null);
    if (invoiceInputRef.current) invoiceInputRef.current.value = "";
  };

  return (
    <AfterSalesLayout>
      <div className="as-register-serial-no-content">
        <div style={{ padding: "0 31.97px" }}>
          {/* ONE BIG CARD */}
          <div className="as-csn_main-card">
            {/* Blue gradient top of the card */}
            <div className="as-csn_header-card">
              <div
                className="as-csn_header-cancel"
                onClick={() =>
                  navigate(
                    `/aftersalesservice${buildAfterSalesQuery(searchParams)}`,
                  )
                }
              >
                <ChevronLeft size={16} />
                {trans.cancel}
              </div>
              <div className="as-csn_shield-icon">
                <img
                  src={Shield}
                  className="as-csn_icon"
                  style={{ width: "80px", height: "80px" }}
                />
              </div>
              <div className="as-csn_header-card-title">{trans.air_con}</div>
              <div className="as-csn_header-card-sub">{trans.warr_reg}</div>
            </div>

            {/* White content area inside the same card */}
            <div className="as-csn_form-area">
              {/* FORM START */}
              <form id="form_detail_brunei" method="post">
                <div
                  id="qr-scan-section"
                  style={{ display: showBuyerSection ? "none" : "block" }}
                >
                  <div className="as-csn-qr-scanned-container">
                    <div className="as-csn-qr-scanned">
                      <img
                        src={QrScanned}
                        className="as-qr_scanned_icon"
                        alt="qr_scanned_icon"
                      />
                      {trans.qr_scanned}
                    </div>
                    <div>{trans.qr_scanned_sub}</div>
                  </div>

                  <div className="as-csn-qr-input-container">
                    <div className="as-csn-qr-input-label">{unit_1}</div>
                    <input
                      type="text"
                      className="as-csn-qr-input"
                      id="reg_sn1"
                      name="reg_sn1"
                      value={sn}
                      readOnly
                    />
                    <img
                      src={QrScanned2}
                      className="as-qr_scanned_2_icon"
                      alt="qr_scanned_2_icon"
                    />
                  </div>

                  <div className="as-csn-qr-input-container as-blue">
                    <div className="as-csn-qr-input-label">{unit_2}</div>
                    <input
                      type="text"
                      className="as-csn-qr-input"
                      id="reg_sn2"
                      name="reg_sn2"
                      placeholder="Tap to scan"
                      readOnly
                      style={{ cursor: "pointer" }}
                      onClick={openScanner}
                    />
                    <img
                      src={ScanQr}
                      className="as-scanqr-icon"
                      alt="scanqr"
                      onClick={openScanner}
                      style={{ cursor: "pointer" }}
                    />
                  </div>

                  <div className="as-csn-qr-line"></div>

                  <div className="as-csn-qr-reminder">{trans.qr_reminder}</div>
                </div>

                <div
                  id="buyer-info-section"
                  style={{ display: showBuyerSection ? "block" : "none" }}
                >
                  <div className="as-csn-qr-input-container as-green">
                    <div className="as-csn-qr-input-label as-green">
                      <img src={QrScanned2} alt="qr_scanned_2_icon" />
                      {trans.units_reg}
                    </div>
                    <div className="as-csn-qr-text">
                      <div className="as-csn-qr-text-label">{unit_1}</div>
                      <div className="as-colon">:</div>
                      <input
                        type="text"
                        className="as-csn-qr-input"
                        id="reg_sn1_copy"
                        name="reg_sn1_copy"
                        value={sn}
                        readOnly
                      />
                    </div>
                    <div className="as-csn-qr-text">
                      <div className="as-csn-qr-text-label">{unit_2}</div>
                      <div className="as-colon">:</div>
                      <input
                        type="text"
                        className="as-csn-qr-input"
                        id="reg_sn2_copy"
                        name="reg_sn2_copy"
                        value={sn2}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="as-csn_section-title">
                    <div
                      className="as-csn_icon-box"
                      style={{ paddingRight: "10px" }}
                    >
                      <img src={Container} />
                    </div>
                    <span className="as-csn_section-text">
                      {trans.purch_details}
                    </span>
                  </div>

                  <div className="as-csn_sub-link as-csn_sub-indent">
                    {trans.purch_details_sub}
                  </div>

                  <div className="as-csn_input-box-container">
                    <div
                      className="as-invoice-upload-box"
                      id="invoice-upload-box"
                      onClick={handleInvoiceUploadClick}
                      style={{ cursor: invoiceFile ? "default" : "pointer" }}
                    >
                      {/* Default upload UI — hidden when file selected */}
                      {!invoiceFile && (
                        <div className="as-invoice-upload-content">
                          <img
                            src={UploadBlue}
                            alt="upload"
                            style={{
                              width: "35px",
                              height: "35px",
                              marginBottom: "15px",
                            }}
                          />
                          <div className="as-invoice-upload-text">
                            {trans.inv_attach}
                          </div>
                          <div className="as-invoice-upload-subtext">
                            {trans.inv_attach_sub}
                          </div>
                        </div>
                      )}

                      {/* File preview — shown when file selected */}
                      {invoiceFile && (
                        <div
                          className="as-invoice-file-preview"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="as-invoice-file-info-container">
                            {/* Image preview (PNG/JPG/JPEG) */}
                            {invoicePreview && (
                              <img
                                src={invoicePreview}
                                alt="Preview"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: "200px",
                                  objectFit: "contain",
                                  borderRadius: "8px",
                                  marginBottom: "10px",
                                }}
                              />
                            )}

                            {/* File info (PDF) */}
                            {!invoicePreview && (
                              <div className="as-invoice-file-info">
                                <img
                                  src={InvAttached}
                                  alt="inv_attached_icon"
                                />
                                <div className="as-invoice-file-type-container">
                                  <span className="as-invoice-file-type">
                                    {invoiceFile.name
                                      .split(".")
                                      .pop()
                                      .toUpperCase()}
                                  </span>
                                  <span className="as-invoice-file-name">
                                    {invoiceFile.name}
                                  </span>
                                </div>
                              </div>
                            )}

                            <button
                              type="button"
                              className="as-invoice-remove-btn"
                              onClick={handleInvoiceRemove}
                            >
                              &times;
                            </button>
                          </div>

                          <div className="as-invoice-file-uploaded">
                            <img src={QrScanned2} alt="qr_scanned_2_icon" />
                            {trans.upload_success}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={invoiceInputRef}
                      id="reg_inv_attach"
                      name="reg_inv_attach"
                      accept=".pdf,.png,.jpg,.jpeg"
                      style={{
                        position: "absolute",
                        opacity: 0,
                        pointerEvents: "none",
                      }}
                      onChange={handleInvoiceFileChange}
                    />
                    {errors.invattach && (
                      <div className="as-error">{errors.invattach}</div>
                    )}
                  </div>

                  <div className="as-csn_section-title">
                    <div
                      className="as-csn_icon-box"
                      style={{ paddingRight: "10px" }}
                    >
                      <img src={Human} />
                    </div>
                    <span className="as-csn_section-text">
                      {trans.contact_details}
                    </span>
                  </div>

                  <div className="as-csn_sub-link as-csn_sub-indent">
                    {trans.contact_details_sub_1}
                  </div>

                  <div className="as-csn_input-box-container">
                    <div className="as-csn_section-text as-csn_field-label as-csn_required">
                      {trans.contact_name}
                    </div>
                    <input
                      type="text"
                      className="as-csn_input-box"
                      id="reg_custname"
                      name="reg_custname"
                      value={warrantyInfo.custname}
                      onChange={(e) => {
                        setWarrantyInfo((p) => ({
                          ...p,
                          custname: e.target.value,
                        }));
                        setErrors((prev) => ({
                          ...prev,
                          custname: "",
                        }));
                      }}
                      maxLength={100}
                    />
                    {errors.custname && (
                      <div className="as-error">{errors.custname}</div>
                    )}
                  </div>

                  <div className="as-csn_input-box-container">
                    <div className="as-csn_section-text as-csn_field-label as-csn_required">
                      {trans.mobile_no}
                    </div>
                    <div className="as-csn_phone-row">
                      <select
                        className="as-csn_input-box as-csn_phone-country-select"
                        value={phoneCountry}
                        onChange={(e) =>
                          handlePhoneCountryChange(e.target.value)
                        }
                        aria-label="Country code"
                      >
                        {PHONE_COUNTRY_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.dial}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        className="as-csn_input-box as-csn_phone-number-input"
                        id="reg_custtelno"
                        name="reg_custtelno"
                        value={warrantyInfo.custtelno}
                        onChange={(e) => handlePhoneInput(e.target.value)}
                        onBlur={(e) => handlePhoneBlur(e.target.value)}
                        maxLength={100}
                        placeholder="Phone number"
                      />
                    </div>
                    {errors.custtelno && (
                      <div className="as-error">{errors.custtelno}</div>
                    )}
                  </div>

                  <div className="as-csn_input-box-container">
                    <div className="as-csn_section-text as-csn_field-label">
                      {trans.email_add}
                    </div>
                    <input
                      type="text"
                      className="as-csn_input-box"
                      id="reg_email"
                      value={warrantyInfo.emailadd}
                      onChange={(e) =>
                        setWarrantyInfo((p) => ({
                          ...p,
                          emailadd: e.target.value,
                        }))
                      }
                      maxLength={100}
                      placeholder="Optional"
                    />
                  </div>

                  <div className="as-csn_checkbox-row">
                    <input
                      type="checkbox"
                      id="agree_policy"
                      checked={agreeWarrantyPolicy}
                      onChange={(e) => setAgreeWarrantyPolicy(e.target.checked)}
                    />
                    <label htmlFor="agree_policy" style={{ fontSize: "13px" }}>
                      {trans.agree_1}
                      <a
                        href="#"
                        onClick={openPdf}
                        style={{
                          color: "#0B5ED7",
                          textDecoration: "underline",
                        }}
                      >
                        {trans.agree_2}
                      </a>
                      {trans.agree_3}
                      <a
                        href="https://file.unity.net.my/document/PDPA+acknowledgement.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#0B5ED7",
                          textDecoration: "underline",
                        }}
                      >
                        {trans.agree_4}
                      </a>
                    </label>
                  </div>

                  <button
                    type="button"
                    className="as-csn_primary-btn"
                    id="submit_btn_brunei"
                    onClick={handleSubmit}
                    disabled={!agreeWarrantyPolicy}
                  >
                    {trans.submit}
                  </button>
                </div>
              </form>
              {/* FORM END */}
            </div>
          </div>
        </div>

        {/* QR Scanner Modal */}
        <div
          className="as-qr-scanner-modal"
          id="qr-scanner-modal"
          style={{ display: showModal ? "flex" : "none" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) stopScanner();
          }}
        >
          <div className="as-qr-scanner-content">
            <div style={{ padding: "20px" }}>
              <div className="as-qr-scanner-title-container">
                <div className="as-qr-scanner-title">{trans.scan_qr_code}</div>
                <button className="as-close-scanner" onClick={stopScanner}>
                  &times;
                </button>
              </div>

              <div className="as-scanner-tabs">
                <button
                  className={`as-scanner-tab ${activeScannerTab === "camera" ? "as-active" : ""}`}
                  onClick={() => switchScannerTab("camera")}
                >
                  {trans.camera}
                </button>
                <button
                  className={`as-scanner-tab ${activeScannerTab === "upload" ? "as-active" : ""}`}
                  onClick={() => switchScannerTab("upload")}
                >
                  {trans.upload_photo}
                </button>
              </div>

              <div
                id="camera-section"
                className={`as-scanner-section ${activeScannerTab === "camera" ? "as-active" : ""}`}
              >
                <div id="qr-reader"></div>
              </div>

              <div
                id="upload-section"
                className={`as-scanner-section ${activeScannerTab === "upload" ? "as-active" : ""}`}
              >
                <div className="as-upload-drop-zone" id="upload-drop-zone">
                  <div className="as-upload-icon">📷</div>
                  <div>
                    <div className="as-upload-text">
                      {trans.upload_photo_sub_1}
                    </div>
                    <div className="as-upload-subtext">
                      {trans.upload_photo_sub_2}
                    </div>
                  </div>
                </div>

                <input
                  type="file"
                  id="qr-file-input"
                  accept="image/*"
                  style={{ display: "none" }}
                />

                <div
                  id="upload-preview"
                  className="as-upload-preview"
                  style={{ display: "none" }}
                >
                  <img id="preview-image" alt="Preview" />
                  <button
                    type="button"
                    id="remove-image"
                    className="as-remove-image-btn"
                  >
                    &times;
                  </button>
                </div>
              </div>

              {scanError && (
                <div
                  className="as-new-install-duplicate-error"
                  style={{ marginTop: "12px" }}
                >
                  <TriangleAlert size={20} />
                  <span>{scanError}</span>
                </div>
              )}

              <div className="as-stop-scanner-container">
                <button className="as-stop-scanner" onClick={stopScanner}>
                  {trans.stop_scanner}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSuccessMessage && (
        <RegisteredReceived serialQuery={buildAfterSalesQuery(searchParams)} />
      )}
      {showMessage && (
        <MultiPurpose
          title="Registration Status"
          message={notifMessage}
          yesText="OK"
          hideNo={true}
          onYes={() => {
            setShowMessage(false);
            window.location.reload();
          }}
          onClose={() => {
            setShowMessage(false);
            window.location.reload();
          }}
        />
      )}
    </AfterSalesLayout>
  );
};

export default RegisterSerialNoBrunei;
